<script setup lang="ts">
/**
 * VisenzeRecommendations Component
 *
 * Fetches and manages product recommendations from Visenze.
 * Supports two types of recommendations: 'Complete the Look' and 'Similar Products'.
 *
 * @component
 * @description Retrieves and renders product recommendations based on a given product SKU
 * @requires useVisenze - Composable for fetching Visenze recommendations
 * @requires useProductTileClPrices - Composable for fetching Commerce Layer prices
 * @requires useGAVisenzeViewItemListEvent - Google Analytics event tracking for recommendations
 *
 * @example
 * <VisenzeRecommendations type="STL" productSku="PRODUCT_123">
 *   <template #default="{ products }">
 *     <!-- Render recommended products -->
 *   </template>
 * </VisenzeRecommendations>
 */
import type {
  VisenzeHookType,
  VisenzeRecommendationsProps,
} from './VisenzeRecommendations.props'

const props = defineProps<VisenzeRecommendationsProps>()

/**
 * Tracks whether events have been sent for each recommendation type
 * @type {Object}
 * @private
 */
const sentEvents = ref({
  STL: false,
  VSR: false,
})

/**
 * Fetch Visenze recommendations using the appropriate method
 * @type {Object}
 * @property {Function} completeTheLook - Fetch 'Complete the Look' recommendations
 * @property {Function} similarProducts - Fetch similar product recommendations
 */
const { completeTheLook, similarProducts } = useVisenze()

/**
 * Async data fetching for product recommendations
 * Selects the appropriate recommendation method based on the type
 *
 * @type {Object} Async data with recommended products
 * @throws {Error} If recommendation fetching fails
 */
const { data } = await useAsyncData(
  `product-visenze-${props.productSku}-${props.type}`,
  async () => {
    if (props.type === 'STL') {
      return await completeTheLook(props.productSku)
    }

    if (props.type === 'VSR') {
      return await similarProducts(props.productSku)
    }

    return { products: [] } as VisenzeHookType
  },
  {
    watch: [() => props.type],
    server: false,
  }
)

/**
 * Extracts default SKU codes from recommended products
 * Filters out null or undefined SKUs
 *
 * @type {ComputedRef<string[]>}
 * @returns {string[]} List of valid SKU codes
 */
const productItemsDefaultSkuCodes = computed(() => {
  if (!data.value?.products) return []
  return data.value?.products
    .map((item: any) => getSKUfromSize(item.data?.size_list?.at(0) || ''))
    .filter(item => isNonNullable<string>(item))
})

/**
 * Fetches Commerce Layer prices for recommended products
 * @type {Object}
 * @property {Ref<Object>} skuPrices - Prices mapped by SKU
 * @property {Function} fetchSkuPrices - Method to fetch prices
 */
const { skuPrices, fetchSkuPrices } = useProductTileClPrices()

/**
 * Fetch Commerce Layer prices when component is mounted
 * Ensures price data is only fetched client-side
 */
onMounted(() => {
  fetchSkuPrices(productItemsDefaultSkuCodes.value)
})

/**
 * Enriches product recommendations with Commerce Layer prices
 *
 * @type {ComputedRef<Array>}
 * @returns {Array} Products with added price information
 */
const productsWithCommerceLayerPrices = computed(() => {
  if (!data.value?.products) return []
  return data.value?.products.map((product: any) => {
    const skuWithSize = getSKUfromSize(product.data?.size_list?.at(0) || '')
    return {
      ...product,
      clPrices: skuPrices.value?.[skuWithSize],
    }
  })
})

/**
 * Tracks Google Analytics view item list event for recommendations
 * Ensures event is sent only once per recommendation type
 */
watch([productsWithCommerceLayerPrices, () => props.type], () => {
  if (
    productsWithCommerceLayerPrices.value?.length > 0 &&
    !sentEvents.value[props.type as 'STL' | 'VSR']
  ) {
    sentEvents.value[props.type as 'STL' | 'VSR'] = true

    useGAVisenzeViewItemListEvent(
      productsWithCommerceLayerPrices.value,
      props.type
    )
  }
})
</script>
<template>
  <clientOnly>
    <slot
      :products="productsWithCommerceLayerPrices"
      :best-image="data?.bestImage"
    />
  </clientOnly>
</template>
